import React, { createContext, useContext, useEffect, useRef } from "react";
import type { SiteConfig } from "@/types/sites";
// import { colorPalettes } from "@/utils/colorPalettes"; // Removed unused import
// Note: ThemeConfig no longer exposes cssVariables/darkVariables. Palette CSS variables
// are applied elsewhere at a global level. This provider only scopes a container ref
// and exposes dark mode state.

interface LandingPageThemeContextValue {
  isDarkMode: boolean;
  setDarkMode: (isDark: boolean) => void;
  containerRef: React.RefObject<HTMLDivElement | null>;
}

const LandingPageThemeContext =
  createContext<LandingPageThemeContextValue | null>(null);

export const useLandingPageTheme = () => {
  const context = useContext(LandingPageThemeContext);
  if (!context) {
    // Provide a fallback for cases where the hook is used outside the provider
    return {
      isDarkMode: false,
      setDarkMode: () => {},
      containerRef: { current: null },
    };
  }
  return context;
};

interface LandingPageThemeProviderProps {
  children: React.ReactNode;
  config: SiteConfig;
  isDarkMode?: boolean;
  onDarkModeChange?: (isDark: boolean) => void;
}

/**
 * Provides scoped dark mode for the landing page preview
 * This ensures dark mode only affects the preview content, not the entire app
 */
export const LandingPageThemeProvider: React.FC<
  LandingPageThemeProviderProps
> = ({ children, config, isDarkMode: propIsDarkMode, onDarkModeChange }) => {
  // Read dark mode setting from config, defaulting to the prop value (if provided) or false
  const isDarkMode = config?.theme?.darkMode ?? propIsDarkMode ?? false;
  const containerRef = useRef<HTMLDivElement>(null);

  // No CSS variables are applied here to avoid referencing non-existent fields on ThemeConfig.
  // Dark mode scoping and palette variable application (if any) are handled by the caller or globally.
  useEffect(() => {
    // intentionally left blank
  }, [isDarkMode, config?.theme]);

  const setDarkMode = (isDark: boolean) => {
    onDarkModeChange?.(isDark);
  };

  return (
    <LandingPageThemeContext.Provider
      value={{ isDarkMode, setDarkMode, containerRef }}
    >
      <div ref={containerRef} style={{ display: "contents" }}>
        {children}
      </div>
    </LandingPageThemeContext.Provider>
  );
};
